import Hls from "hls.js";
import { useEffect, useState } from "react";
import StationLogo from "../components/StationLogo";
import type { Station } from "../lib/m3u";
import { isBlockedMixedContent, resolvePlaylist, streamType } from "../lib/streams";
import { usePlayer } from "./PlayerContext";

type Status = "loading" | "playing" | "paused" | "error";

function useStream(audio: HTMLAudioElement | null, station: Station | null) {
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!audio || !station) return;

    const controller = new AbortController();
    let hls: Hls | null = null;

    const fail = (message: string) => {
      if (controller.signal.aborted) return;
      setError(message);
      setStatus("error");
    };

    const start = async () => {
      setError(null);
      setStatus("loading");

      let url = station.url;
      if (streamType(url) === "unsupported") return fail("This stream format isn't supported in the browser.");

      if (streamType(url) === "playlist") {
        try {
          url = await resolvePlaylist(url, controller.signal);
        } catch {
          // Often blocked by CORS; some servers serve audio at the playlist URL anyway.
          if (controller.signal.aborted) return;
        }
      }

      if (isBlockedMixedContent(url)) return fail("This stream uses insecure HTTP and can't play on an HTTPS page.");

      const nativeHls = audio.canPlayType("application/vnd.apple.mpegurl") !== "";
      if (streamType(url) === "hls" && !nativeHls) {
        if (!Hls.isSupported()) return fail("HLS streams aren't supported in this browser.");
        hls = new Hls();
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) fail("The stream couldn't be loaded.");
        });
        hls.loadSource(url);
        hls.attachMedia(audio);
      } else {
        audio.src = url;
      }

      try {
        await audio.play();
      } catch (playError) {
        if (controller.signal.aborted) return;
        // Autoplay was blocked: leave it paused so the play button works.
        if (playError instanceof DOMException && playError.name === "NotAllowedError") setStatus("paused");
        else fail("The stream couldn't be played.");
      }
    };

    const onPlaying = () => setStatus("playing");
    const onPause = () => setStatus((current) => (current === "error" ? current : "paused"));
    const onWaiting = () => setStatus("loading");
    const onError = () => fail("The stream couldn't be loaded.");

    audio.addEventListener("playing", onPlaying);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("error", onError);
    start();

    return () => {
      controller.abort();
      audio.removeEventListener("playing", onPlaying);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("error", onError);
      hls?.destroy();
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    };
  }, [audio, station]);

  return { status, error };
}

const statusLabel: Record<Status, string> = {
  loading: "Connecting…",
  playing: "Live",
  paused: "Paused",
  error: "Error",
};

export default function Player() {
  const { station, stop } = usePlayer();
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [volume, setVolume] = useState(1);
  const { status, error } = useStream(audio, station);

  useEffect(() => {
    if (audio) audio.volume = volume;
  }, [audio, volume]);

  const togglePlay = () => {
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => undefined);
    else audio.pause();
  };

  return (
    <>
      <audio ref={setAudio} preload="none" />
      {station && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white/95 shadow-lg backdrop-blur">
          <div className="mx-auto flex max-w-3xl items-center gap-4 px-4 py-3">
            <StationLogo station={station} className="size-12 text-lg" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-slate-900">{station.name}</p>
              <p className={`truncate text-sm ${status === "error" ? "text-red-700" : "text-slate-500"}`}>
                {status === "error" ? error : statusLabel[status]}
              </p>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(event) => setVolume(Number(event.target.value))}
              aria-label="Volume"
              className="hidden w-24 accent-sky-600 sm:block"
            />
            <button
              type="button"
              onClick={togglePlay}
              disabled={status === "error"}
              aria-label={status === "playing" || status === "loading" ? "Pause" : "Play"}
              className="flex size-10 items-center justify-center rounded-full bg-sky-600 text-white hover:bg-sky-700 disabled:bg-slate-300"
            >
              {status === "playing" || status === "loading" ? "❚❚" : "▶"}
            </button>
            <button
              type="button"
              onClick={stop}
              aria-label="Close player"
              className="flex size-10 items-center justify-center rounded-full text-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </>
  );
}
